import { useState } from 'react';
import type { CSSProperties } from 'react';
interface StartScreenProps {
  onStart: () => void;
  bestScore?: number;
}
export function StartScreen({ onStart, bestScore = 0 }: StartScreenProps) {
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-sky-200 to-sky-100 p-6">
      {/* Title */}
      <div className="mb-4 text-center">
        <h1 className="text-4xl font-bold text-amber-700 mb-2 drop-shadow-sm">
          Catch the Star
        </h1>
        <p className="text-amber-600/80 text-lg">A relaxing catching game</p>
      </div>
      {/* Best score badge */}
      {bestScore > 0 && (
        <div className="mb-6">
          <div className="bg-amber-200/60 rounded-full px-4 py-1.5 shadow-sm border border-amber-300/50">
            <span className="text-amber-800 font-semibold text-sm">
              Best: {bestScore}
            </span>
          </div>
        </div>
      )}
      {/* Decorative stars */}
      <div className="flex gap-4 mb-10">
        <span
          className="text-5xl animate-bounce"
          style={{ WebkitTapHighlightColor: 'transparent', animationDelay: '0s' } as CSSProperties}
        >
          ⭐
        </span>
        <span
          className="text-6xl animate-bounce"
          style={{ WebkitTapHighlightColor: 'transparent', animationDelay: '0.15s' } as CSSProperties}
        >
          ⭐
        </span>
        <span
          className="text-5xl animate-bounce"
          style={{ WebkitTapHighlightColor: 'transparent', animationDelay: '0.3s' } as CSSProperties}
        >
          ⭐
        </span>
      </div>
      {/* Buttons */}
      <div className="flex flex-col gap-4 w-full max-w-xs">
        <button
          onClick={onStart}
          className="w-full bg-amber-400 hover:bg-amber-500 active:bg-amber-600 text-amber-900 font-bold text-xl py-4 px-8 rounded-2xl shadow-lg border-4 border-amber-500 transition-all duration-150 -translate-y-1 active:translate-y-0 active:shadow-md"
          style={{
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none',
            touchAction: 'manipulation',
          } as CSSProperties}
        >
          Start Game
        </button>
        <button
          onClick={() => setShowHowToPlay(true)}
          className="w-full bg-amber-50 hover:bg-amber-100 active:bg-amber-200 text-amber-700 font-semibold text-lg py-3 px-6 rounded-xl shadow-md border-2 border-amber-200 transition-all duration-150"
          style={{
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none',
            touchAction: 'manipulation',
          } as CSSProperties}
        >
          How to Play
        </button>
      </div>
      {/* How to Play Modal */}
      {showHowToPlay && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setShowHowToPlay(false)}
        >
          <div
            className="bg-amber-50 rounded-3xl p-6 max-w-sm w-full shadow-2xl border-4 border-amber-300"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-amber-700 mb-4 text-center">
              How to Play
            </h2>
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🧺</span>
                <p className="text-amber-800">
                  Move the basket left and right to catch falling stars
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">⭐</span>
                <p className="text-amber-800">
                  Catch as many stars as you can to increase your score
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">📱</span>
                <p className="text-amber-800">
                  <strong>Mobile:</strong> Drag or tap to move the basket
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">⌨️</span>
                <p className="text-amber-800">
                  <strong>Desktop:</strong> Use arrow keys or A/D to move
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowHowToPlay(false)}
              className="w-full bg-amber-400 hover:bg-amber-500 active:bg-amber-600 text-amber-900 font-bold text-lg py-3 px-6 rounded-xl shadow-md border-2 border-amber-500 transition-all duration-150"
              style={{
                WebkitTapHighlightColor: 'transparent',
                userSelect: 'none',
                touchAction: 'manipulation',
              } as CSSProperties}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}